import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CursoFormDialog } from "@/components/forms/CursoFormDialog";
import { Plus, Trash2, FolderKanban, Pencil } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  ativo: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  planejado: "bg-sky-500/20 text-sky-300 border-sky-500/40",
  encerrado: "bg-slate-500/20 text-slate-300 border-slate-500/40",
};

export default function Cursos() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [cursos, setCursos] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/cursos?tipo=curso").then((r) => setCursos(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setOpen(true); };
  const openEdit = (e, c) => { e.stopPropagation(); setEditing(c); setOpen(true); };
  const remove = async (e, id) => { e.stopPropagation(); await api.delete(`/cursos/${id}`); toast.success("Curso removido"); load(); };

  return (
    <div>
      <button data-testid="voltar-btn" onClick={() => navigate("/")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-3">
        <span aria-hidden>←</span> Voltar
      </button>
      <PageHeader title="Cursos" subtitle="Formações e treinamentos cadastrados">
        {isAdmin && <Button data-testid="novo-curso-btn" onClick={openNew}><Plus className="h-4 w-4 mr-2" />Novo curso</Button>}
      </PageHeader>

      <div className="grid sm:grid-cols-2 gap-4">
        {cursos.map((c) => (
          <Card key={c.id} data-testid={`curso-${c.id}`} onClick={() => navigate(`/cursos/${c.id}`)}
            className="p-5 bg-card border-border cursor-pointer hover:border-primary/60 hover:bg-card/70 transition-all">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-sky-500/15"><FolderKanban className="h-5 w-5 text-sky-400" /></div>
                <div>
                  <div className="font-semibold text-lg leading-tight">{c.nome}</div>
                  <Badge className={`mt-1 ${STATUS_BADGE[c.status] || "bg-secondary text-secondary-foreground border-border"}`}>{c.status}</Badge>
                </div>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-2 shrink-0">
                  <button data-testid={`edit-curso-${c.id}`} onClick={(e) => openEdit(e, c)} className="text-muted-foreground hover:text-primary"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`del-curso-${c.id}`} onClick={(e) => remove(e, c.id)} className="text-muted-foreground hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                </div>
              )}
            </div>
            {c.descricao && <p className="text-sm text-muted-foreground mt-3">{c.descricao}</p>}
            <div className="flex flex-wrap gap-4 mt-3 text-xs text-muted-foreground font-mono">
              {c.periodo && <span>Período: {c.periodo}</span>}
              {c.carga_horaria && <span>{c.carga_horaria}</span>}
              {c.usa_tipologia_cec && <span className="text-primary">Tipologia CEC</span>}
            </div>
          </Card>
        ))}
        {!cursos.length && <p className="text-muted-foreground text-sm">Nenhum curso cadastrado.</p>}
      </div>

      <CursoFormDialog open={open} onOpenChange={setOpen} curso={editing} onSaved={load} />
    </div>
  );
}
